import React, { useEffect, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { LenderPerformanceReportInput } from '@trnsact/trnsact-shared-types';
import { useLenderPerformanceApprovalTerms } from '../../../../hooks/useLenderPerformanceApprovalTerms';
import { TERMS_COMPARISON_DEFAULT_SORTING } from '../../../../constants';
import { ConversionTableSorting } from '../../../../types';
import { ApprovalTermsSummaryCards } from './ApprovalTermsSummaryCards';
import { LenderTermsComparisonTable } from './LenderTermsComparisonTable';
import { AverageRateByLenderChart } from './AverageRateByLenderChart';
import { TermDistributionByLenderChart } from './TermDistributionByLenderChart';
import { TotalCostComparisonChart } from './TotalCostComparisonChart';

interface Props {
  input: LenderPerformanceReportInput;
  onLoadingChange?: (isLoading: boolean) => void;
}

export const ApprovalTerms = ({ input, onLoadingChange }: Props) => {
  const [sorting, setSorting] = useState<ConversionTableSorting>(TERMS_COMPARISON_DEFAULT_SORTING);

  const {
    summary,
    comparison,
    averageRate,
    termDistribution,
    totalCost,
    isSummaryLoading,
    isComparisonLoading,
    isAverageRateLoading,
    isTermDistributionLoading,
    isTotalCostLoading,
  } = useLenderPerformanceApprovalTerms(input, sorting);

  const isLoading =
    isSummaryLoading || isComparisonLoading || isAverageRateLoading || isTermDistributionLoading || isTotalCostLoading;

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  return (
    <Stack gap="1.5rem">
      <ApprovalTermsSummaryCards summary={summary} loading={isSummaryLoading} />

      <Box data-pdf-page-break>
        <LenderTermsComparisonTable
          rows={comparison}
          loading={isComparisonLoading}
          sorting={sorting}
          onSortingChange={setSorting}
        />
      </Box>

      <Box data-pdf-page-break data-pdf-no-break>
        <AverageRateByLenderChart rows={averageRate} loading={isAverageRateLoading} />
      </Box>

      <Box data-pdf-page-break data-pdf-no-break>
        <TermDistributionByLenderChart rows={termDistribution} loading={isTermDistributionLoading} />
      </Box>

      <Box data-pdf-page-break data-pdf-no-break>
        <TotalCostComparisonChart rows={totalCost} loading={isTotalCostLoading} />
      </Box>
    </Stack>
  );
};
